import React, { useEffect, useMemo, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import { selectCurrentZone } from '../../store/global'
import NavigationHeader from 'components/NavigationHeader/NavigationHeader'
import { Flex, Spin } from 'antd'
import { CurrentZone } from 'utils/types'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import cx from 'classnames'
import messages from './messages'
import { useEcommerceExtendedEmptyColumnsQuery } from 'hooks/useEmptyEcommerceExtendedColumnsQuery'
import { EcommerceExtendedEntityForTable, IEcommerceExtendedSummary } from './types'
import { filtersInitialState } from 'containers/EcommerceExtendedTablePage/EcommerceExtended.config'
import { useDebounce } from 'hooks/useDebounce'
import { Filter, FilterAdditionalData } from 'components/FilterDrawer/FilterDrawer.config'
import { useFilterOptionsQuery } from 'containers/EcommerceExtendedTablePage/useFiltersOptionsQuery'
import { SellerCartCheckStatus } from 'containers/VerticalPage/common/types'
import { enumToObject } from 'utils/enumToObject'
import { useEcommerceExtendedOverviewQuery } from './useEcommerceExtendedOverviewQuery'
import EcommerceExtendedSummary from './EcommerceExtendedSummary'
import { useEcommerceExtendedTableQuery } from 'containers/EcommerceExtendedTablePage/useEcommerceExtendedTableQuery'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import Search from 'UI/Search/Search'
import ExportCSVTableVertical from 'components/ExportCSVTableVertical/ExportCSVTableVertical'
import { ecommerceExtendedColumns } from './utils'
import VerticalTableFilters from 'components/VerticalTableFilters/VerticalTableFilters'
import { PAGE_SIZE } from 'components/verticals/constants'
import styles from 'styles/TablePageCommon.scss'

const EcommerceExtendedTablePage = () => {
  const currentZone: CurrentZone = useSelector(selectCurrentZone)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const statisticsActivityRef = useRef<HTMLDivElement | null>(null)
  const navigationRef = useRef<HTMLDivElement | null>(null)

  const [activeFilters, setActiveFilters] = useState<Filter[]>([])
  const [filterAdditionalData, setFilterAdditionalData] = useState<FilterAdditionalData[]>([])

  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const activeFiltersDebounced = useDebounce(activeFilters, 500)

  const { filterOptions } = useFilterOptionsQuery({
    verticalIndices: currentZone?.ecommerceExtendedIndices,
    currentQueryKey: 'ecommerce-extended-filter-options-key',
    filtersInitialState,
  })

  const { searchValue, searchValueDebounced, sorting, setSearchValue, setSorting } = useVerticalSearchAndSort()

  const { data: emptyColumnsData, isFetched: emptyColumnsIsFetched } = useEcommerceExtendedEmptyColumnsQuery({
    verticalIndices: currentZone?.ecommerceExtendedIndices,
    currentQueryKey: 'ecommerce-extended-entities-key',
  })

  const { data: overviewData } = useEcommerceExtendedOverviewQuery({
    verticalIndices: currentZone?.ecommerceExtendedIndices,
    currentQueryKey: 'ecommerce-extended-overview-key',
    searchValue: searchValueDebounced,
    filters: activeFiltersDebounced,
  })

  useEffect(() => {
    if (!filterOptions || !overviewData) return
    setFilterAdditionalData([
      { name: 'filters.cartCheckStatus.keyword', activeFilterLabelMap: enumToObject(SellerCartCheckStatus) },
    ])
  }, [filterOptions, overviewData])

  const { verticalEntities, fetchNextPage, isFetching, isLoading, isError } =
    useEcommerceExtendedTableQuery<EcommerceExtendedEntityForTable>({
      verticalIndices: currentZone?.ecommerceExtendedIndices,
      currentQueryKey: 'ecommerce-extended-entities-key',
      searchValue: searchValueDebounced,
      filters: activeFiltersDebounced,
      sorting,
      pageSize: PAGE_SIZE,
    })

  const overviewSummaryData = useMemo(() => {
    if (!overviewData) return null
    const overviewDataCopy = { ...overviewData }
    delete overviewDataCopy.total
    return overviewDataCopy
  }, [overviewData])

  return (
    <div ref={containerRef} className={styles.container}>
      <div ref={navigationRef}>
        <nav id='navigationHeaderWrapper' className={styles.navigationHeaderWrapper}>
          <NavigationHeader />
        </nav>
        <header className='page-header withNavigationHeader'>
          <h1 className={cx('page-title', 'h1')}>
            <FormattedMessage {...messages.header} />
          </h1>
        </header>
      </div>
      <section ref={statisticsActivityRef} id={'businessSummary'}>
        <EcommerceExtendedSummary summaryData={overviewSummaryData as IEcommerceExtendedSummary} />
      </section>
      {isLoading ? (
        <Flex align='center' gap='middle' justify='center' style={{ padding: '45px 14px' }}>
          <Spin size='large' />
        </Flex>
      ) : isError ? (
        <Alert severity='error' sx={{ fontFamily: 'Source Sans Pro', fontSize: '1rem', alignItems: 'center' }}>
          Something went wrong
        </Alert>
      ) : (
        <>
          {emptyColumnsIsFetched && (
            <VerticalTable
              pageSize={PAGE_SIZE}
              enableColumnPicker
              dataSource={verticalEntities as any[]}
              columns={ecommerceExtendedColumns}
              containerRef={containerRef}
              additionalRefs={[statisticsActivityRef, navigationRef]}
              fetchNextPage={fetchNextPage}
              total={overviewData?.total || 0}
              loading={isFetching}
              disabledColumns={emptyColumnsData}
              onSelectionChange={setSelectedIds}
              removeFromPickerColumns={[COLUMNS_IDS.profileNames]}
              rowClassName={styles.tableRow}
              onRow={record => {
                return {
                  onClick: () => {
                    window.open(`${window.location.origin}/vertical/ecommerce-extended/${record.id}`, '_blank')
                  },
                }
              }}
              onChange={(pagination, filters, sorter) => {
                if (!Array.isArray(sorter)) {
                  if (sorter.order) {
                    setSorting([{ id: sorter.columnKey as string, desc: sorter.order === 'descend' }])
                  } else {
                    setSorting([])
                  }
                }
              }}
            >
              <div className={styles.counter}>
                {`Ecommerce: `}
                <span>{selectedIds.length > 0 ? `${selectedIds.length} selected` : overviewData?.total}</span>
              </div>
              <VerticalTableFilters
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                filtersInitialState={filtersInitialState}
                filterOptions={filterOptions}
                filterAdditionalData={filterAdditionalData}
              />
              <Search
                placeholder='input search text'
                enterButton
                allowClear
                style={{ width: 264 }}
                loading={isFetching}
                onSearch={newSearchValue => setSearchValue(newSearchValue)}
              />
              <ExportCSVTableVertical
                zoneIndex={currentZone?.ecommerceExtendedIndices}
                csvFileName='Ecommerce'
                searchValue={searchValue}
                selectedIds={selectedIds}
                sorting={sorting}
              />
            </VerticalTable>
          )}
        </>
      )}
    </div>
  )
}

export default EcommerceExtendedTablePage
